import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  startOfToday,
  parse,
  isValid,
  addDays,
  getDay,
  format,
  differenceInCalendarDays,
} from "date-fns";
import {
  CalendarClock,
  CalendarRange,
  Dice5,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DatePicker } from "../../common/DatePicker";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FieldError } from "@/components/ui/field";
import { useFacilityCourses } from "modules/course/courseHooks";
import { useFacilityRoundConfigurations } from "modules/round-configuration/roundConfigurationHooks";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import {
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueWizardScheduleMode,
  Clubhouse_LgRoundStatusV2,
  TeeType,
} from "../../../enums";
import { OptionCard } from "../../common/OptionCard";
import { ProgressiveRow, ProgressiveRowGroup } from "../ProgressiveRow";
import { wizardLayout } from "../wizardLayout";
import { RoundPlanTable, type RoundPlanTableRow } from "../RoundPlanTable";
import toast from "react-hot-toast";
import { RoundEditorDialog } from "../RoundEditorDialog";
import { RoundPresetSheet } from "../RoundPresetSheet";
import { CreateRoundPresetSheet } from "../CreateRoundPresetSheet";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import {
  roundPresetToRoundSettings,
  withSettingsSource,
} from "../roundPresetSettings";
import { useRoundPlan } from "../useRoundPlan";
import {
  generateRoundPlan,
  planRoundToSeasonItem,
  mergePlanWithEdits,
  WEEKLY_WINDOW_REQUIRED,
  scheduleWeekDayForNewRound,
  weeklyPlayWindowForNewRound,
} from "../roundPlan";
import {
  useGetLeagueRoundsQuery,
  useUpdateLeagueRoundMutation,
  useCreateLeagueRoundMutation,
  useDeleteLeagueRoundMutation,
} from "../../../api/endpoints/rounds";
import {
  extractServerErrorLines,
  isSeasonFlexAllowed,
  parseSelectedHolesJson,
  resolveCourseId,
  roundSettingsById,
  seasonFlexDisabledReason,
} from "../../helpers";
import {
  toLocalDateOnly,
  toPatchRoundBody,
  toCreateRoundBody,
  toDateOnly,
} from "../roundMapping";
import { ApplyRoundSettingsDialog } from "../ApplyRoundSettingsDialog";
import { useApplyRoundSettingsPrompt } from "../useApplyRoundSettingsPrompt";
import { TEE_OPTIONS } from "../roundSettingsOptions";
import type { SeasonRoundItem } from "../../leagueFormSchema";
import type { AdvancedLeagueFormValues } from "../advancedLeagueSchema";

const { WeeklyRounds, SeasonFlex } = Clubhouse_LeagueWizardScheduleMode;

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const revealCls =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-300";

type RowKey =
  | "seasonDates"
  | "scheduleMode"
  | "weekDay"
  | "mensTee"
  | "womensTee"
  | "roundSettings"
  | "rounds";

function toDate(
  iso?: string,
  boundary: "start" | "end" = "start",
  timezone?: string,
): Date | undefined {
  if (!iso) return undefined;
  const dateOnly = toLocalDateOnly(iso, timezone, boundary);
  const d = parse(dateOnly?.slice(0, 10) ?? "", "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

const LEAGUE_TEES = [
  TeeType.RED,
  TeeType.GOLD,
  TeeType.WHITE,
  TeeType.BLUE,
  TeeType.BLACK,
] as const;

const leagueTeeLabel = (value?: number) =>
  TEE_OPTIONS.find((option) => option.value === value)?.label;

const TEE_DOT: Record<number, string> = {
  [TeeType.RED]: "bg-red-600",
  [TeeType.GOLD]: "bg-amber-400",
  [TeeType.WHITE]: "bg-white",
  [TeeType.BLUE]: "bg-blue-600",
  [TeeType.BLACK]: "bg-zinc-900",
};

function LeagueTeeRow({
  label,
  rowKey,
  value,
  hint,
  className,
  openOnCreate,
  rowOpen,
  setRowOpen,
  onSelect,
  error,
}: {
  label: string;
  rowKey: RowKey;
  value?: number | null;
  hint: string;
  className?: string;
  openOnCreate: boolean;
  rowOpen: (key: RowKey, auto: boolean) => boolean;
  setRowOpen: (key: RowKey) => (open: boolean) => void;
  onSelect: (tee: TeeType) => void;
  error?: { message?: string };
}) {
  const hasValue = value != null;
  return (
    <ProgressiveRow
      label={label}
      className={className}
      settled={hasValue}
      value={hasValue ? leagueTeeLabel(value) : "Choose a tee"}
      open={rowOpen(rowKey, openOnCreate || !hasValue)}
      onOpenChange={setRowOpen(rowKey)}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {LEAGUE_TEES.map((tee) => (
            <button
              key={tee}
              type="button"
              aria-pressed={value === (tee as number)}
              onClick={() => onSelect(tee)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                value === (tee as number)
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-surface text-text-muted hover:border-primary/40",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "size-2.5 rounded-full ring-1 ring-inset ring-foreground/55",
                  TEE_DOT[tee],
                )}
              />
              {leagueTeeLabel(tee)}
            </button>
          ))}
        </div>
        <p className="text-sm text-text-muted">{hint}</p>
        <FieldError errors={error ? [error] : undefined} />
      </div>
    </ProgressiveRow>
  );
}

export function ScheduleDatesTab({
  isEdit = false,
  leagueId,
  onRoundPlanBuildingChange,
}: {
  isEdit?: boolean;
  leagueId?: string | null;
  /** Create flow: true while the generated plan is waiting on facility courses. */
  onRoundPlanBuildingChange?: (building: boolean) => void;
} = {}) {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<AdvancedLeagueFormValues>();

  const today = startOfToday();
  const start = useWatch({ control, name: "leagueStartDate" });
  const end = useWatch({ control, name: "leagueEndDate" });
  const scheduleMode = useWatch({ control, name: "scheduleMode" });
  const weekDay = useWatch({ control, name: "weekDay" });
  const seasonRoundCount = useWatch({ control, name: "seasonRoundCount" });
  const scoringType = useWatch({ control, name: "settings.leagueScoringType" });
  const seasonRounds = useWatch({ control, name: "seasonRounds" });
  const schedulingTimezone = useWatch({
    control,
    name: "leagueStartDateTimezone",
  });
  const mensTee = useWatch({ control, name: "settings.mensTee" });
  const womensTee = useWatch({ control, name: "settings.womensTee" });

  const leagueScoring = scoringType ?? Clubhouse_LeagueScoring.STROKE;
  const isMatch = scoringType === Clubhouse_LeagueScoring.MATCH;
  const seasonFlexDisabled = !isSeasonFlexAllowed(scoringType);
  const seasonFlexDisabledMessage = seasonFlexDisabledReason(scoringType);

  const facilityId = useGetCurrentLocalFacilityId();

  // Generated rounds start from the facility's default conditions — the same
  // settings this facility already uses everywhere else in Clubhouse — so the
  // operator isn't editing every round to say the same thing N times. A preset
  // is the operator's own named configuration, so a league is set up by picking
  // the one it runs on. Absent choice means randomize, which needs no setup.
  const [presets, presetsQueryState] = useFacilityRoundConfigurations(
    facilityId ?? "",
  );
  // Loading and genuinely-empty are different answers. Without this the empty
  // state claims the facility has no presets while they're still in flight,
  // and the adopt-on-create snapshot below would be taken against an empty
  // list — so the query resolving would look like the operator's own save.
  const presetsLoaded = !!presetsQueryState?.isFinished;
  const roundSettingsSource = useWatch({
    control,
    name: "roundSettingsSource",
  });
  const roundPresetId = useWatch({ control, name: "roundPresetId" });
  const usingPreset = roundSettingsSource === "preset";
  const presetOptions = useMemo(
    () =>
      [...presets].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      ),
    [presets],
  );
  const selectedPreset = useMemo(
    () => presetOptions.find((p) => p.id === roundPresetId),
    [presetOptions, roundPresetId],
  );
  // Randomize varies the conditions and nothing else, so it layers over the
  // chosen preset when there is one — a preset's automation still applies while
  // each round's weather is scattered.
  const seededSettings = useMemo(
    () => roundPresetToRoundSettings(selectedPreset),
    [selectedPreset],
  );
  const [presetSheetOpen, setPresetSheetOpen] = useState(false);
  const [createPresetOpen, setCreatePresetOpen] = useState(false);

  // Adopt a preset authored from the tray. The create form owns its own submit
  // and reports nothing back, but its mutation merges into the entity slice
  // this list reads — so the new preset is whichever id wasn't there when the
  // tray opened. Selecting it is the whole point of creating it here.
  const presetIdsBeforeCreate = useRef<Set<string> | null>(null);
  const openCreatePreset = () => {
    // Only arm the watcher once the list is real; an empty snapshot would
    // adopt whichever preset the query happens to return first.
    presetIdsBeforeCreate.current = presetsLoaded
      ? new Set(presetOptions.map((p) => p.id))
      : null;
    setCreatePresetOpen(true);
  };
  useEffect(() => {
    const known = presetIdsBeforeCreate.current;
    if (!known) return;
    const created = presets.find((p) => !known.has(p.id));
    if (!created) return;
    presetIdsBeforeCreate.current = null;
    setCreatePresetOpen(false);
    setValue("roundSettingsSource", "preset", { shouldDirty: true });
    setValue("roundPresetId", created.id, { shouldDirty: true });
  }, [presets, setValue]);

  // Records the choice and nothing else — re-seeding the already-generated
  // rounds is the effect below, which covers this and the case a click handler
  // can't see (the presets query landing after the rounds were generated).
  //
  // Ignores a click on the card that is already selected: OptionCard fires
  // onClick regardless of `selected`, and re-seeding is destructive, so a
  // visual no-op must not throw away a per-round edit. Unanswered (undefined)
  // never matches either source, so the first click always records a real
  // choice rather than being swallowed as a no-op against an assumed default.
  const chooseSettingsSource = (source: "preset" | "randomize") => {
    if (roundSettingsSource === source) return;
    setValue("roundSettingsSource", source, { shouldDirty: true });
  };

  const [courses, coursesQueryState] = useFacilityCourses(facilityId ?? "");
  const coursesLoaded = !!coursesQueryState?.isFinished;
  const courseNameById = useMemo(
    () => new Map(courses.map((c) => [String(c.id), c.displayName])),
    [courses],
  );

  // Match Play requires fixed round windows, so Season Flex is cleared when
  // that scoring format is selected. Don't dirty on edit:
  // correcting a hydrated value shouldn't flag unsaved changes the operator
  // never made.
  useEffect(() => {
    if (seasonFlexDisabled && scheduleMode !== WeeklyRounds) {
      setValue("scheduleMode", WeeklyRounds, { shouldDirty: !isEdit });
    }
  }, [seasonFlexDisabled, scheduleMode, setValue, isEdit]);

  const [editingRoundNumber, setEditingRoundNumber] = useState<number | null>(
    null,
  );

  // Rounds the operator has explicitly edited (for the "Custom" tag) — distinct
  // from seasonRounds, which now holds the full generated plan.
  const [editedNumbers, setEditedNumbers] = useState<Set<number>>(
    () => new Set(),
  );

  // Generated rounds the operator removed from the not-yet-saved plan. The
  // plan effect below always regenerates the full set from the season's dates
  // and schedule mode, so exclusion has to live outside `seasonRounds` or the
  // next render would silently bring the round back.
  const [removedRoundNumbers, setRemovedRoundNumbers] = useState<Set<number>>(
    () => new Set(),
  );
  const [removingRoundNumber, setRemovingRoundNumber] = useState<number | null>(
    null,
  );

  // A round number is only the plan's array position (see generateRoundPlan),
  // reassigned from scratch whenever the season's dates or schedule mode
  // change — so a removal keyed on it goes stale the moment those inputs
  // change: "round 1" after the change is a different round than the one that
  // was removed. Clear the removed set whenever the inputs that regenerate
  // the plan change, rather than let it silently mask an unrelated new round.
  const planIdentity = `${start}|${end}|${scheduleMode}|${weekDay}`;
  const appliedPlanIdentity = useRef<string | null>(null);
  useEffect(() => {
    if (appliedPlanIdentity.current === null) {
      appliedPlanIdentity.current = planIdentity;
      return;
    }
    if (appliedPlanIdentity.current === planIdentity) return;
    appliedPlanIdentity.current = planIdentity;
    setRemovedRoundNumbers(new Set());
  }, [planIdentity]);

  const [openRows, setOpenRows] = useState<Partial<Record<RowKey, boolean>>>(
    {},
  );
  const rowOpen = (key: RowKey, auto: boolean) => openRows[key] ?? auto;
  const setRowOpen = (key: RowKey) => (open: boolean) =>
    setOpenRows((o) => ({ ...o, [key]: open }));

  const startD = toDate(start, "start", schedulingTimezone);
  const endD = toDate(end, "end", schedulingTimezone);
  const endMinDate = startD ? addDays(startD, 1) : today;
  const datesSet = !!startD && !!endD;

  const isWeekly = scheduleMode === WeeklyRounds;
  const isFlex = scheduleMode === SeasonFlex;

  // Flex creates one round per season week (⌈days ÷ 7⌉, at least one) — ceil so a
  // partial final week still gets its own round (an 8-day season is 2 rounds,
  // not 1). The operator no longer types a count; they can add rounds from the
  // Rounds table, which bumps `seasonRoundCount` above this baseline. Capped at
  // 52 to match generateRoundPlan, so the displayed count never overstates the
  // rounds made.
  const seasonWeeks =
    startD && endD
      ? Math.max(1, Math.ceil((differenceInCalendarDays(endD, startD) + 1) / 7))
      : 0;
  const flexRoundCount = Math.min(seasonRoundCount ?? seasonWeeks, 52);

  // Flex derives its count from the dates, so choosing a mode is enough.
  const scheduleAnswered = scheduleMode != null;
  const weekDayAnswered = isWeekly ? weekDay != null : true;
  // Men's/women's tees and Round Settings are each their own required choice
  // (no silent default) — the Rounds table only reveals once the operator has
  // actually answered every row that feeds it. Edit mode does NOT generate
  // rounds — it would show regenerated courses/windows as if persisted, so the
  // generated table is create-only until real rounds are loaded on edit.
  const teesAnswered = mensTee != null && womensTee != null;
  // Choosing "Use a saved Round Preset" isn't itself a complete answer — the
  // operator still has to pick which preset from the dropdown. Randomize has
  // no further sub-choice, so picking that card alone is the full answer.
  const roundSettingsAnswered =
    roundSettingsSource === "randomize" ||
    (roundSettingsSource === "preset" && !!roundPresetId);
  const roundsReady =
    !isEdit &&
    datesSet &&
    scheduleAnswered &&
    weekDayAnswered &&
    teesAnswered &&
    roundSettingsAnswered;

  const { plan, coursesReady } = useRoundPlan({
    seasonStart: start,
    seasonEnd: end,
    scheduleMode: scheduleMode ?? undefined,
    weekDay: weekDay ?? undefined,
    flexRoundCount,
    settings: seededSettings,
    varyConditions: !usingPreset,
    ready: roundsReady,
  });
  // Create only. Edit shows saved rounds and already refuses course-less writes.
  const planBuilding = roundsReady && !coursesReady;

  useEffect(() => {
    onRoundPlanBuildingChange?.(planBuilding);
    return () => onRoundPlanBuildingChange?.(false);
  }, [planBuilding, onRoundPlanBuildingChange]);

  const overrideByNumber = useMemo(
    () => new Map((seasonRounds ?? []).map((r) => [r.roundNumber, r])),
    [seasonRounds],
  );

  // The generated plan is the source of truth for what gets created: mirror it
  // into `seasonRounds` (preserving per-round edits by number) so the whole set
  // ships in the create POST and the backend persists it instead of generating.
  // A round the operator removed is filtered out here — otherwise this effect
  // would regenerate the full plan on the next unrelated re-render and quietly
  // bring it back.
  useEffect(() => {
    if (!plan || plan.rounds.length === 0) return;
    const current = getValues("seasonRounds") ?? [];
    const keptPlanRounds = plan.rounds.filter(
      (r) => !removedRoundNumbers.has(r.roundNumber),
    );
    const next = mergePlanWithEdits(keptPlanRounds, current, leagueScoring);
    // Only write when the set actually changed — `plan` can be a fresh object
    // on every render (e.g. the courses selector), and an unconditional
    // setValue would loop.
    if (JSON.stringify(next) !== JSON.stringify(current)) {
      setValue("seasonRounds", next, { shouldDirty: true });
    }
  }, [plan, removedRoundNumbers, getValues, setValue, leagueScoring]);

  // Re-seed the generated rounds when the settings source changes underneath
  // them. The plan can't do this on its own: `mergePlanWithEdits` keeps the
  // settings already stored on a round (which is what stops a tab switch wiping
  // per-round edits), so a regenerated plan carrying new settings is discarded
  // and the diff above short-circuits. Three cases reach here:
  //   - the operator picks the other Round Settings card;
  //   - they set or edit the facility defaults from the tray, which is the
  //     common one — most facilities have none, and the row promises "every
  //     round starts from these";
  //   - the defaults query resolves after the rounds were generated.
  // Keyed on the seed's *value*, so an identical reload re-seeds nothing.
  const seedSignature = `${usingPreset}|${JSON.stringify(seededSettings ?? null)}`;
  const appliedSeed = useRef<string | null>(null);
  useEffect(() => {
    const rounds = getValues("seasonRounds") ?? [];
    // Nothing generated yet: the plan seeds those itself, so just record where
    // the seed stood rather than treating first generation as a change.
    if (rounds.length === 0 || appliedSeed.current === null) {
      appliedSeed.current = seedSignature;
      return;
    }
    // Choosing the preset card is not itself a new source — the preset is.
    // Rewriting here would strip the rounds back to nothing for as long as the
    // dropdown sits unanswered, and ship them that way if the operator saved
    // the draft in between. Deliberately doesn't record the signature, so
    // picking a preset still registers as the change it is.
    if (usingPreset && !selectedPreset) return;
    if (appliedSeed.current === seedSignature) return;
    appliedSeed.current = seedSignature;
    setValue(
      "seasonRounds",
      withSettingsSource(rounds, seededSettings, !usingPreset),
      { shouldDirty: true },
    );
    // The rewrite replaces each round's settings, so any per-round edit is gone
    // — the "Custom" tag is the only record the operator has of which rounds
    // they touched, and leaving it up would point them at edits that no longer
    // exist.
    setEditedNumbers(new Set());
  }, [
    seedSignature,
    seededSettings,
    usingPreset,
    selectedPreset,
    getValues,
    setValue,
  ]);

  // Merge the generated plan with any per-round edits the operator has saved.
  // Rounds the operator removed are excluded here too, so the sync effect
  // above and the visible table always agree on what's left.
  const tableRows: RoundPlanTableRow[] = useMemo(() => {
    if (!plan) return [];
    return plan.rounds
      .filter((pr) => !removedRoundNumbers.has(pr.roundNumber))
      .map((pr) => {
        const edit = overrideByNumber.get(pr.roundNumber);
        const courseId = edit?.courseId || pr.courseId;
        const courseName = courseId
          ? (courseNameById.get(String(courseId)) ?? pr.courseName)
          : pr.courseName;
        const winStart = edit?.playWindowStartUtc ?? pr.playWindowStartUtc;
        const winEnd = edit?.playWindowEndUtc ?? pr.playWindowEndUtc;
        const settings = edit?.settings ?? pr.settings;
        const holeCount = settings?.selectedHoles?.length || 9;

        const winStartD = toDate(winStart, "start", schedulingTimezone);
        const winEndD = toDate(winEnd, "end", schedulingTimezone);
        const dueD = toDate(pr.dueByUtc, "end", schedulingTimezone);
        const windowLabel =
          isFlex || !winStartD
            ? `Due ${dueD ? format(dueD, "MMM d") : "—"}`
            : `${format(winStartD, "MMM d")} – ${format(winEndD ?? winStartD, "MMM d")}`;
        const windowSub =
          !isFlex && winStartD && winEndD
            ? `${DAY_ABBR[getDay(winStartD)]} – ${DAY_ABBR[getDay(winEndD)]}`
            : undefined;

        return {
          id: String(pr.roundNumber),
          roundNumber: pr.roundNumber,
          courseName,
          windowLabel,
          windowSub,
          holesLabel: `${holeCount} holes`,
          custom: editedNumbers.has(pr.roundNumber),
        };
      });
  }, [
    plan,
    removedRoundNumbers,
    overrideByNumber,
    courseNameById,
    isFlex,
    editedNumbers,
    schedulingTimezone,
  ]);

  // Seed the drawer from the saved edit if present, else from the plan round.
  const editingRound: SeasonRoundItem | undefined = useMemo(() => {
    if (editingRoundNumber == null || !plan) return undefined;
    const existing = overrideByNumber.get(editingRoundNumber);
    if (existing) return existing;
    const pr = plan.rounds.find((r) => r.roundNumber === editingRoundNumber);
    return pr ? planRoundToSeasonItem(pr, leagueScoring) : undefined;
  }, [editingRoundNumber, plan, overrideByNumber, leagueScoring]);

  // Flex operators can pad the schedule beyond the per-week baseline (up to the
  // 52-round cap); the plan regenerates with the extra round appended.
  const handleAddFlexRound = () =>
    setValue("seasonRoundCount", Math.min(flexRoundCount + 1, 52), {
      shouldDirty: true,
    });

  const handleSaveRound = (values: SeasonRoundItem) => {
    // Course and conditions are the operator's. The play window stays the one
    // the schedule generated — it isn't editable on create.
    const planned = plan?.rounds.find(
      (round) => round.roundNumber === values.roundNumber,
    );
    const scheduled = planned
      ? planRoundToSeasonItem(planned, leagueScoring)
      : undefined;
    const saved: SeasonRoundItem = {
      ...values,
      playWindowStartUtc: scheduled?.playWindowStartUtc ?? "",
      playWindowEndUtc: scheduled?.playWindowEndUtc ?? "",
      ...(scheduled?.dueByUtc ? { dueByUtc: scheduled.dueByUtc } : {}),
    };
    const next = [
      ...(seasonRounds ?? []).filter(
        (r) => r.roundNumber !== values.roundNumber,
      ),
      saved,
    ].sort((a, b) => a.roundNumber - b.roundNumber);
    setValue("seasonRounds", next, { shouldDirty: true });
    setEditedNumbers((s) => new Set(s).add(values.roundNumber));
    setEditingRoundNumber(null);
  };

  // ── Edit mode: load and PATCH the league's actual saved rounds ────────────
  const [editingRoundId, setEditingRoundId] = useState<string | null>(null);
  const [addingRound, setAddingRound] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [updateRound] = useUpdateLeagueRoundMutation();
  const [createRound] = useCreateLeagueRoundMutation();
  const [deleteRound, { isLoading: isArchivingRound }] =
    useDeleteLeagueRoundMutation();
  const [archiveRoundId, setArchiveRoundId] = useState<string | null>(null);

  const {
    data: roundsData,
    isLoading: roundsLoading,
    refetch: refetchRounds,
  } = useGetLeagueRoundsQuery(
    { facilityId: facilityId ?? "", leagueId: leagueId ?? "" },
    { skip: !isEdit || !facilityId || !leagueId },
  );

  const { maybePrompt: maybePromptApply, dialogProps: applyDialogProps } =
    useApplyRoundSettingsPrompt({
      facilityId,
      leagueId: leagueId ?? undefined,
      rounds: roundsData,
      courses,
    });

  const editTableRows: RoundPlanTableRow[] = useMemo(
    () =>
      [...(roundsData ?? [])]
        .map((r) => {
          // Read each round's own settings/holes directly (no per-row lookup).
          const holeCount =
            parseSelectedHolesJson(r.selectedHolesJson)?.length || 9;
          const s = toDate(r.playWindowStartUtc, "start", schedulingTimezone);
          const e = toDate(r.playWindowEndUtc, "end", schedulingTimezone);
          return {
            id: r.id != null ? String(r.id) : String(r.roundNumber ?? 0),
            roundNumber: r.roundNumber ?? 0,
            courseName: r.courseName ?? "",
            // A draft switch to Flex (not yet saved) should read as due by the
            // season end immediately, even though the saved round still
            // carries its old window until the operator actually saves the
            // change — mirrors how the generated (create-flow) rows show a
            // Flex round's due date.
            windowLabel: isFlex
              ? `Due ${endD ? format(endD, "MMM d") : "—"}`
              : s
                ? `${format(s, "MMM d")} – ${format(e ?? s, "MMM d")}`
                : "—",
            windowSub:
              !isFlex && s && e
                ? `${DAY_ABBR[getDay(s)]} – ${DAY_ABBR[getDay(e)]}`
                : undefined,
            holesLabel: `${holeCount} holes`,
            // Only windowed (Fixed-time) rounds are editable in the drawer today;
            // a Flex round has no window and can't be represented yet.
            editable: !!s && r.id != null,
            // A Finalized round is locked; archiving it isn't offered — same
            // rule as the rounds table on the League Detail page.
            removable:
              r.id != null && r.status !== Clubhouse_LgRoundStatusV2.Finalized,
          };
        })
        .sort((a, b) => a.roundNumber - b.roundNumber),
    [roundsData, schedulingTimezone, isFlex, endD],
  );

  const editRoundValues: SeasonRoundItem | undefined = useMemo(() => {
    if (!editingRoundId) return undefined;
    // The sheet seeds its form once, on the open transition. Wait for the
    // course list so the name→id resolve below has something to match against;
    // opening first would seed courseId 0 and never re-seed.
    if (!coursesLoaded) return undefined;
    const dto = roundsData?.find((r) => String(r.id) === editingRoundId);
    if (!dto) return undefined;
    return {
      roundId: editingRoundId,
      roundNumber: dto.roundNumber ?? 0,
      name: dto.name ?? "",
      competitionType: leagueScoring,
      playWindowStartUtc:
        toLocalDateOnly(dto.playWindowStartUtc, schedulingTimezone, "start") ??
        "",
      playWindowEndUtc:
        toLocalDateOnly(dto.playWindowEndUtc, schedulingTimezone, "end") ?? "",
      // The DTO's courseId is a slug the picker can't match, so resolve the
      // numeric Course.id by name — otherwise the Course select opens empty.
      courseId: resolveCourseId(dto.courseName, courses),
      settings: roundSettingsById(roundsData, editingRoundId),
    };
  }, [
    editingRoundId,
    roundsData,
    leagueScoring,
    schedulingTimezone,
    courses,
    coursesLoaded,
  ]);

  const handleEditRoundSave = async (values: SeasonRoundItem) => {
    if (!editingRoundId || !leagueId || !facilityId) return;
    await updateRound({
      leagueId,
      facilityId,
      roundId: editingRoundId,
      body: toPatchRoundBody(values, {
        timezone: schedulingTimezone,
        includePlayWindow: false,
        original: roundsData?.find(
          (round) => String(round.id) === editingRoundId,
        ),
      }),
    }).unwrap();
    const editedId = editingRoundId;
    setEditingRoundId(null);
    toast.success("Round updated.", {
      position: "top-center",
      className: "text-sm poppins",
    });
    maybePromptApply(editedId, values.settings);
  };

  const handleWeekDayChange = async (nextWeekDay: number) => {
    if (nextWeekDay === weekDay) return;

    if (!isEdit) {
      setValue("weekDay", nextWeekDay, { shouldDirty: true });
      return;
    }

    if (roundsLoading || roundsData == null) {
      toast.error("Saved rounds are still loading.");
      return;
    }

    // The cascade PATCHes every round at once, each carrying a course resolved
    // by name. Running before the course list lands would resolve all of them
    // to 0 and drop the course from every PATCH in the batch.
    if (!coursesLoaded) {
      toast.error("Courses are still loading.");
      return;
    }

    const previousWeekDay = weekDay;
    if (!leagueId || !facilityId || !start || !end || roundsData.length === 0) {
      setValue("weekDay", nextWeekDay, { shouldDirty: false });
      return;
    }

    const plan = generateRoundPlan({
      seasonStart: start,
      seasonEnd: end,
      scheduleMode: WeeklyRounds,
      weekDay: nextWeekDay,
    });
    const planByNumber = new Map(
      plan.rounds.map((round) => [round.roundNumber, round]),
    );
    const unmappedRounds = roundsData.filter(
      (round) =>
        round.id == null ||
        round.roundNumber == null ||
        !planByNumber.has(round.roundNumber),
    );
    if (unmappedRounds.length > 0) {
      toast.error(
        "That weekday would leave saved rounds outside the season window. Choose another day.",
      );
      return;
    }

    const updates = roundsData.flatMap((round) => {
      if (round.id == null || round.roundNumber == null) return [];
      const planned = planByNumber.get(round.roundNumber);
      if (!planned?.playWindowStartUtc || !planned.playWindowEndUtc) {
        return [];
      }

      const item: SeasonRoundItem = {
        roundId: String(round.id),
        roundNumber: round.roundNumber,
        name: round.name ?? "",
        competitionType: leagueScoring,
        playWindowStartUtc: planned.playWindowStartUtc,
        playWindowEndUtc: planned.playWindowEndUtc,
        // Rescheduling only moves the play window. The DTO's courseId is a slug
        // the write contract can't take, so resolve it by name; an unresolved 0
        // is dropped from the PATCH and the round keeps the course it has.
        courseId: resolveCourseId(round.courseName, courses),
        settings: roundSettingsById(roundsData, String(round.id)),
      };
      return [
        updateRound({
          leagueId,
          facilityId,
          roundId: String(round.id),
          body: toPatchRoundBody(item, {
            timezone: schedulingTimezone,
            original: round,
          }),
        }).unwrap(),
      ];
    });

    setIsRescheduling(true);
    try {
      await Promise.all(updates);
      setValue("weekDay", nextWeekDay, { shouldDirty: false });
      toast.success("Round play windows updated.", {
        position: "top-center",
        className: "text-sm poppins",
      });
    } catch {
      // Some PATCHes may have succeeded before another failed. Refresh the
      // saved rows so the UI reflects persisted state instead of implying a
      // client-only rollback restored the server.
      await refetchRounds();
      setValue("weekDay", previousWeekDay, { shouldDirty: false });
      toast.error("Couldn't update all round play windows.");
    } finally {
      setIsRescheduling(false);
    }
  };

  // Pencil handler: the row id is the round number (create) or the backend
  // round id (edit), so route on mode without re-looking-up the round.
  const handleEditPencil = (id: string) => {
    if (isEdit) setEditingRoundId(id);
    else setEditingRoundNumber(Number(id));
  };

  // Remove handler: same id-based routing as the pencil above. Edit mode opens
  // the archive confirmation (backend-owned, DELETE on confirm); create mode
  // opens the lighter remove confirmation for a round still only in state.
  const handleRemoveRound = (id: string) => {
    if (isEdit) setArchiveRoundId(id);
    else setRemovingRoundNumber(Number(id));
  };

  const handleRemoveGeneratedRound = (roundNumber: number) => {
    setRemovedRoundNumbers((s) => new Set(s).add(roundNumber));
    setEditedNumbers((s) => {
      if (!s.has(roundNumber)) return s;
      const next = new Set(s);
      next.delete(roundNumber);
      return next;
    });
    setRemovingRoundNumber(null);
  };

  async function handleArchiveSavedRound() {
    if (!archiveRoundId || !facilityId || !leagueId) return;
    try {
      await deleteRound({
        facilityId,
        leagueId,
        roundId: archiveRoundId,
      }).unwrap();
      setArchiveRoundId(null);
      toast.success("Round archived.", {
        position: "top-center",
        className: "text-sm poppins",
      });
    } catch (err) {
      setArchiveRoundId(null);
      const msg = extractServerErrorLines(err).join("\n");
      toast.error(msg || "Couldn't archive the round. Please try again.");
    }
  }

  // Edit mode: append a brand-new round via POST. The next number follows the
  // highest saved round (so it slots after the current set), and the create
  // invalidates the League tag — the rounds query refetches and the new row
  // appears without any local bookkeeping.
  const nextRoundNumber =
    (roundsData ?? []).reduce(
      (max, r) => Math.max(max, r.roundNumber ?? 0),
      0,
    ) + 1;

  const scheduleWeekDayForAppend = useMemo(() => {
    if (isFlex) return undefined;
    if (!isEdit) return weekDay ?? undefined;
    const ordered = [...(roundsData ?? [])].sort(
      (a, b) => (a.roundNumber ?? 0) - (b.roundNumber ?? 0),
    );
    return scheduleWeekDayForNewRound({
      roundWindowStarts: ordered.map((round) => round.playWindowStartUtc),
      seasonStart: start,
      timezone: schedulingTimezone,
      toLocalDate: toLocalDateOnly,
    });
  }, [isFlex, isEdit, weekDay, roundsData, start, schedulingTimezone]);
  const lastRoundPlayWindowEndLocal = useMemo(() => {
    if (isFlex || !roundsData?.length) return undefined;
    const last = [...roundsData].sort(
      (a, b) => (a.roundNumber ?? 0) - (b.roundNumber ?? 0),
    ).at(-1);
    return (
      toLocalDateOnly(
        last?.playWindowEndUtc,
        schedulingTimezone,
        "end",
      ) ?? undefined
    );
  }, [isFlex, roundsData, schedulingTimezone]);
  const assignedPlayWindow = useMemo(
    () =>
      !isFlex &&
      start &&
      end &&
      scheduleWeekDayForAppend != null
        ? weeklyPlayWindowForNewRound({
            seasonStart: start,
            seasonEnd: end,
            weekDay: scheduleWeekDayForAppend,
            roundNumber: nextRoundNumber,
            lastRoundPlayWindowEndLocal,
          })
        : undefined,
    [
      isFlex,
      start,
      end,
      scheduleWeekDayForAppend,
      nextRoundNumber,
      lastRoundPlayWindowEndLocal,
    ],
  );

  const handleAddRoundSave = async (values: SeasonRoundItem) => {
    if (!leagueId || !facilityId) return;
    if (!isFlex && (!values.playWindowStartUtc || !values.playWindowEndUtc)) {
      throw { data: { detail: WEEKLY_WINDOW_REQUIRED } };
    }
    await createRound({
      leagueId,
      facilityId,
      body: toCreateRoundBody(values, {
        timezone: schedulingTimezone,
        // This tab only has the league's date-only start/end (no fetched
        // league detail to source a UTC instant from), so convert them here —
        // a Flex round with no window of its own falls back to these as its
        // play window, and the create endpoint rejects a round with no window
        // at all.
        seasonStartUtc: toDateOnly(start, "start", {
          timezone: schedulingTimezone,
        }),
        seasonEndUtc: toDateOnly(end, "end", { timezone: schedulingTimezone }),
      }),
    }).unwrap();
    setAddingRound(false);
    toast.success("Round added.", {
      position: "top-center",
      className: "text-sm poppins",
    });
  };

  const seasonLength = useMemo(() => {
    if (!startD || !endD) return null;
    const days = differenceInCalendarDays(endD, startD) + 1;
    return `${seasonWeeks} week${seasonWeeks === 1 ? "" : "s"} · ${days} days`;
  }, [startD, endD, seasonWeeks]);

  const scheduleValue =
    scheduleMode == null
      ? "Choose a schedule"
      : isWeekly
        ? "Fixed-time · Weekly"
        : `Flex · ${flexRoundCount} round${flexRoundCount === 1 ? "" : "s"}`;

  return (
    <ProgressiveRowGroup>
      {/* ── Season Dates ─────────────────────────────────────────────── */}
      <ProgressiveRow
        label="Season Dates"
        settled={datesSet}
        value={
          startD && endD
            ? `${format(startD, "MMM d")} – ${format(endD, "MMM d, yyyy")}`
            : "Set season dates"
        }
        open={rowOpen("seasonDates", !datesSet)}
        onOpenChange={setRowOpen("seasonDates")}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
            <div className="flex w-full flex-col gap-1.5 sm:w-56">
              <label
                htmlFor="leagueStartDate"
                className="poppins text-sm font-medium tracking-tight"
              >
                Start date
              </label>
              <Controller
                name="leagueStartDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    id="leagueStartDate"
                    value={field.value}
                    onChange={field.onChange}
                    minDate={today}
                    placeholder="Select start date"
                    aria-invalid={!!errors.leagueStartDate}
                  />
                )}
              />
              <FieldError
                errors={
                  errors.leagueStartDate ? [errors.leagueStartDate] : undefined
                }
              />
            </div>
            <div className="flex w-full flex-col gap-1.5 sm:w-56">
              <label
                htmlFor="leagueEndDate"
                className="poppins text-sm font-medium tracking-tight"
              >
                End date
              </label>
              <Controller
                name="leagueEndDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    id="leagueEndDate"
                    value={field.value}
                    onChange={field.onChange}
                    minDate={endMinDate}
                    placeholder="Select end date"
                    aria-invalid={!!errors.leagueEndDate}
                  />
                )}
              />
              <FieldError
                errors={
                  errors.leagueEndDate ? [errors.leagueEndDate] : undefined
                }
              />
            </div>
          </div>
          {seasonLength && (
            <p className="text-sm text-text-muted">
              Season length: {seasonLength}
            </p>
          )}
        </div>
      </ProgressiveRow>

      {/* ── Schedule Mode ────────────────────────────────────────────── */}
      {datesSet && (
        <ProgressiveRow
          label="Schedule Mode"
          className={revealCls}
          settled={scheduleAnswered}
          locked={isMatch}
          value={scheduleValue}
          open={rowOpen("scheduleMode", !scheduleAnswered)}
          onOpenChange={setRowOpen("scheduleMode")}
        >
          {isMatch ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-text-muted">
                Match Play needs a shared play window, so scheduling is
                Fixed-time.
              </p>
              <WeeklyCard />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className={wizardLayout.choices}>
                <OptionCard
                  className={wizardLayout.choice}
                  icon={<CalendarClock className="size-6" />}
                  title="Fixed-time"
                  description="Everyone plays the same week"
                  align="left"
                  compact
                  selected={isWeekly}
                  onClick={() =>
                    setValue("scheduleMode", WeeklyRounds, {
                      shouldDirty: true,
                    })
                  }
                />
                <OptionCard
                  className={wizardLayout.choice}
                  icon={<CalendarRange className="size-6" />}
                  title="Flex"
                  description="Players finish on their own time"
                  align="left"
                  compact
                  selected={isFlex}
                  disabled={seasonFlexDisabled}
                  onClick={() =>
                    setValue("scheduleMode", SeasonFlex, { shouldDirty: true })
                  }
                />
              </div>
              {seasonFlexDisabledMessage && (
                <p className="text-sm text-text-muted">
                  {seasonFlexDisabledMessage}
                </p>
              )}
              {isWeekly && <WeeklyCard />}
              {isFlex && (
                <div className="flex w-fit max-w-full items-center gap-4 rounded-xl border border-border bg-muted/30 px-4 py-3">
                  <div className="flex flex-col">
                    <span className="poppins text-sm font-semibold tracking-tight text-text-primary">
                      One round per week
                    </span>
                    <span className="text-xs text-text-muted">
                      Players finish each round on their own time.
                    </span>
                  </div>
                  <span className="rounded-full bg-secondary/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary poppins">
                    {flexRoundCount} round{flexRoundCount === 1 ? "" : "s"}
                  </span>
                </div>
              )}
            </div>
          )}
        </ProgressiveRow>
      )}

      {/* ── Week Day (Fixed-time only) ───────────────────────────────── */}
      {datesSet && isWeekly && (
        <ProgressiveRow
          label="Week Day"
          className={revealCls}
          settled={weekDay != null}
          value={
            weekDay != null ? `Every ${DAY_FULL[weekDay]}` : "Choose a day"
          }
          open={rowOpen("weekDay", weekDay == null)}
          onOpenChange={setRowOpen("weekDay")}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {DAY_ABBR.map((abbr, i) => (
                <button
                  key={abbr}
                  type="button"
                  aria-pressed={weekDay === i}
                  aria-label={DAY_FULL[i]}
                  onClick={() => void handleWeekDayChange(i)}
                  disabled={
                    isRescheduling ||
                    (isEdit && (roundsLoading || roundsData == null))
                  }
                  className={cn(
                    "flex size-11 items-center justify-center rounded-full border text-sm font-medium transition-colors",
                    weekDay === i
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-surface text-text-muted hover:border-primary/40",
                  )}
                >
                  {abbr}
                </button>
              ))}
            </div>
            {weekDay != null && (
              <p className="text-sm text-text-muted">
                Opens {DAY_FULL[weekDay]}, closes {DAY_FULL[(weekDay + 6) % 7]}{" "}
                night.
              </p>
            )}
          </div>
        </ProgressiveRow>
      )}

      {datesSet && scheduleAnswered && weekDayAnswered && (
        <Controller
          control={control}
          name="settings.mensTee"
          render={({ field }) => (
            <LeagueTeeRow
              label="Men's Tee"
              rowKey="mensTee"
              value={field.value}
              hint="A player can still get a different tee until Round 1 starts."
              className={revealCls}
              openOnCreate={!isEdit}
              rowOpen={rowOpen}
              setRowOpen={setRowOpen}
              onSelect={(tee) => field.onChange(tee)}
              error={errors.settings?.mensTee}
            />
          )}
        />
      )}

      {datesSet && scheduleAnswered && weekDayAnswered && (
        <Controller
          control={control}
          name="settings.womensTee"
          render={({ field }) => (
            <LeagueTeeRow
              label="Women's Tee"
              rowKey="womensTee"
              value={field.value}
              hint="A player can still get a different tee until Round 1 starts."
              className={revealCls}
              openOnCreate={!isEdit}
              rowOpen={rowOpen}
              setRowOpen={setRowOpen}
              onSelect={(tee) => field.onChange(tee)}
              error={errors.settings?.womensTee}
            />
          )}
        />
      )}

      {/* ── Round Settings ───────────────────────────────────────────── */}
      {/* Gated on tees too (not just dates/schedule) in create mode: the
          operator works through Tee Selection before Round Settings is
          offered. Edit mode has no tee-selection step of its own, so it
          isn't gated on teesAnswered. */}
      {datesSet &&
        scheduleAnswered &&
        weekDayAnswered &&
        (isEdit || teesAnswered) && (
          <ProgressiveRow
            label="Round Settings"
            className={revealCls}
            settled={roundSettingsAnswered}
            value={
              roundSettingsSource == null
                ? "Choose round settings"
                : usingPreset
                  ? (selectedPreset?.name ?? "Pick a preset")
                  : "Randomized"
            }
            open={rowOpen("roundSettings", !roundSettingsAnswered)}
            onOpenChange={setRowOpen("roundSettings")}
          >
            <div className="flex flex-col gap-3">
              <div className={wizardLayout.choices}>
                <OptionCard
                  className={wizardLayout.choice}
                  icon={<SlidersHorizontal className="size-6" />}
                  title="Use a saved Round Preset"
                  description="One of this facility's own configurations"
                  align="left"
                  compact
                  selected={roundSettingsSource === "preset"}
                  onClick={() => chooseSettingsSource("preset")}
                />
                <OptionCard
                  className={wizardLayout.choice}
                  icon={<Dice5 className="size-6" />}
                  title="Randomize course settings for me"
                  description="We'll vary the course conditions each round"
                  align="left"
                  compact
                  selected={roundSettingsSource === "randomize"}
                  onClick={() => chooseSettingsSource("randomize")}
                />
              </div>

            {usingPreset &&
              (!presetsLoaded ? (
                <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                  <p className="text-xs text-text-muted">
                    Loading this facility&rsquo;s Round Presets…
                  </p>
                </div>
              ) : presetOptions.length === 0 ? (
                <div className="flex w-fit max-w-full flex-col gap-2 rounded-xl border border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
                  <p className="text-xs text-text-muted">
                    This facility has no saved Round Presets yet. Create one to
                    reuse on any league, or leave Randomize selected.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 rounded-xl"
                    onClick={openCreatePreset}
                  >
                    New preset
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 rounded-xl border border-border bg-muted/30 px-4 py-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Select
                      value={roundPresetId ?? undefined}
                      onValueChange={(id) =>
                        setValue("roundPresetId", id ?? undefined, {
                          shouldDirty: true,
                        })
                      }
                    >
                      <SelectTrigger
                        id="round-preset"
                        aria-label="Round Preset"
                        className="w-72 rounded-xl"
                      >
                        {/* Render the name ourselves: the items only mount when
                            the dropdown opens, so a controlled value would show
                            the preset's raw id until first open. */}
                        <SelectValue placeholder="Pick a preset…">
                          {selectedPreset?.name}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {presetOptions.map((preset) => (
                          <SelectItem key={preset.id} value={preset.id}>
                            {preset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 rounded-xl"
                      disabled={!selectedPreset}
                      onClick={() => setPresetSheetOpen(true)}
                    >
                      View settings
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 rounded-xl"
                      onClick={openCreatePreset}
                    >
                      New preset
                    </Button>
                  </div>
                  <p className="text-xs text-text-muted">
                    Every round starts from this preset&rsquo;s conditions and
                    automation. Course and holes stay per round; tees come from
                    the league&rsquo;s men&rsquo;s and women&rsquo;s tees.
                  </p>
                </div>
              ))}
          </div>
        </ProgressiveRow>
      )}

      {/* ── Rounds ───────────────────────────────────────────────────── */}
      {/* Create generates the plan; edit shows the league's saved rounds and,
          even with none yet, still renders so the operator can add the first. */}
      {(isEdit ? !!leagueId : roundsReady && (plan != null || planBuilding)) && (
        <ProgressiveRow
          label="Rounds"
          className={revealCls}
          settled={!planBuilding}
          value={
            planBuilding
              ? "Building…"
              : isEdit && roundsLoading
                ? "Loading…"
                : isEdit && editTableRows.length === 0
                  ? "No rounds yet"
                  : `${(isEdit ? editTableRows : tableRows).length} rounds`
          }
          open={rowOpen("rounds", true)}
          onOpenChange={setRowOpen("rounds")}
        >
          <div className="relative flex flex-col gap-3">
            {planBuilding && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-xl bg-background/80">
                <Spinner className="size-5" />
                <p className="text-sm text-text-muted">Building the round plan</p>
              </div>
            )}
            <div
              className={cn(
                "flex flex-col gap-3",
                planBuilding && "pointer-events-none opacity-50",
              )}
              aria-busy={planBuilding || undefined}
            >
            {(isEdit ? editTableRows.length > 0 : !planBuilding) && (
              <p className="text-sm text-text-muted">
                {isEdit
                  ? "Edit any single round without leaving this screen."
                  : "Generated automatically from the settings above — edit any single round without leaving this screen."}
              </p>
            )}
            {planBuilding ||
            (isEdit && roundsLoading && editTableRows.length === 0) ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-xl" />
                ))}
              </div>
            ) : isEdit && editTableRows.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-text-muted mulish">
                No rounds yet — add your first round to get started.
              </p>
            ) : (
              <RoundPlanTable
                rows={isEdit ? editTableRows : tableRows}
                onEdit={handleEditPencil}
                onRemove={handleRemoveRound}
                removeLabel={isEdit ? "Archive" : "Remove"}
              />
            )}
            {(isEdit || isFlex) && (
              <button
                type="button"
                disabled={planBuilding}
                onClick={
                  isEdit ? () => setAddingRound(true) : handleAddFlexRound
                }
                className="flex items-center justify-center gap-1.5 self-start rounded-lg border border-dashed border-border px-3 py-2 text-sm font-medium text-text-muted transition-colors hover:border-primary/40 hover:text-text-primary poppins disabled:pointer-events-none disabled:opacity-40"
              >
                <Plus className="size-4" />
                Add round
              </button>
            )}
            </div>
          </div>
        </ProgressiveRow>
      )}

      <RoundEditorDialog
        key={(isEdit ? editingRoundId : editingRoundNumber) ?? "none"}
        mode="edit"
        open={
          isEdit
            ? editingRoundId != null && !!editRoundValues
            : editingRoundNumber != null && !!editingRound
        }
        onOpenChange={(open) => {
          if (!open) {
            setEditingRoundId(null);
            setEditingRoundNumber(null);
          }
        }}
        onSave={isEdit ? handleEditRoundSave : handleSaveRound}
        initialValues={isEdit ? editRoundValues : editingRound}
        leagueScoringType={leagueScoring}
        seasonEndDate={end}
        isFlex={isFlex}
      />

      {isEdit && (
        <RoundEditorDialog
          key={addingRound ? `add-${nextRoundNumber}` : "add-none"}
          mode="create"
          open={addingRound}
          onOpenChange={(open) => {
            if (!open) setAddingRound(false);
          }}
          onSave={handleAddRoundSave}
          nextRoundNumber={nextRoundNumber}
          facilityId={facilityId ?? undefined}
          leagueScoringType={leagueScoring}
          seasonEndDate={end}
          assignedPlayWindow={assignedPlayWindow}
          isFlex={isFlex}
        />
      )}

      <RoundPresetSheet
        open={presetSheetOpen}
        onOpenChange={setPresetSheetOpen}
        preset={selectedPreset}
      />

      <CreateRoundPresetSheet
        open={createPresetOpen}
        onOpenChange={(open) => {
          // Abandoning the form must not leave the adopt-on-create watcher
          // armed, or the next preset made anywhere would be selected here.
          if (!open) presetIdsBeforeCreate.current = null;
          setCreatePresetOpen(open);
        }}
      />

      {isEdit && <ApplyRoundSettingsDialog {...applyDialogProps} />}

      <ConfirmDialog
        open={removingRoundNumber !== null}
        onOpenChange={(open) => {
          if (!open) setRemovingRoundNumber(null);
        }}
        title="Remove this round?"
        description="This round hasn't been saved yet — removing it takes it out of the generated schedule and it won't be created when you save."
        confirmLabel="Remove round"
        onConfirm={() => {
          if (removingRoundNumber != null) {
            handleRemoveGeneratedRound(removingRoundNumber);
          }
        }}
      />

      <ConfirmDialog
        open={archiveRoundId !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveRoundId(null);
        }}
        title="Archive this round?"
        description="A scheduled round with no play activity is permanently deleted. Rounds with play activity are kept as cancelled, not deleted. This can't be undone."
        confirmLabel="Archive round"
        isLoading={isArchivingRound}
        onConfirm={() => void handleArchiveSavedRound()}
      />
    </ProgressiveRowGroup>
  );
}

function WeeklyCard() {
  return (
    <div className="flex w-fit max-w-full items-center gap-4 rounded-xl border border-border bg-muted/30 px-4 py-3">
      <div className="flex flex-col">
        <span className="poppins text-sm font-semibold tracking-tight text-text-primary">
          Weekly
        </span>
        <span className="text-xs text-text-muted">One round every week.</span>
      </div>
      <span className="rounded-full bg-secondary/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary poppins">
        Fixed for 1.0
      </span>
    </div>
  );
}
